import type { PointerState, NodeDesignData } from '@/modules/map-editor';
import type { Node } from '@/modules/map-editor';
import { getShape } from '@/modules/Node';
import { SIZER_POINTS } from '@/models/node/SIZER_POINTS';

/**
 * Service responsible for node resize calculations and persistence.
 *
 * Handles all sizing-related logic: computing new dimensions based on
 * mouse movement and sizer-point position, enforcing max-size constraints,
 * and persisting the final rounded dimensions when the drag ends.
 */
export class NodeResizeService {
  /** Accumulated resize dimensions during a drag */
  private dimensions: NodeDesignData = {};

  /** Width/height ratio captured at drag start for shift-constrained resize */
  private aspectRatio: number | null = null;

  /** Hard ceiling for node width/height in pixels */
  private readonly MAX_NODE_SIZE = 2200;

  /**
   * @param state - Shared reactive pointer state (read/write)
   */
  constructor(private readonly state: PointerState) {}

  /**
   * Initialise resize state when the user begins a sizing drag.
   *
   * @param widthHeightRatio - Current width / height of the node being resized
   */
  startResize(widthHeightRatio: number): void {
    this.aspectRatio = widthHeightRatio;
    this.dimensions = {};
  }

  /**
   * Recalculate node dimensions for an in-progress resize drag.
   *
   * @param node            - The node being resized
   * @param movementX       - Horizontal delta since last frame
   * @param movementY       - Vertical delta since last frame
   * @param isShiftPressed  - Whether shift is held (constrain aspect ratio)
   * @param pixelRatio      - `window.devicePixelRatio`
   * @param nodeDesignUpdate - Callback that pushes the design change into Vuex
   */
  performResize(
    node: Node,
    movementX: number,
    movementY: number,
    isShiftPressed: boolean,
    pixelRatio: number,
    nodeDesignUpdate: (designData: NodeDesignData) => void
  ): void {
    this.nodeSizeChange(
      node,
      movementX,
      movementY,
      isShiftPressed,
      pixelRatio,
      this.aspectRatio || 1,
      nodeDesignUpdate
    );
  }

  /**
   * Finalize a node resize operation: round dimensions, persist, and reset.
   *
   * @param activeNode       - The node that was being resized (may be null)
   * @param nodeDesignUpdate - Optional callback to push design change into Vuex
   * @param saveNodeDesign   - Callback to persist design to backend
   * @returns `true` if sizing was handled (caller should return early)
   */
  async finalize(
    activeNode: Node | null,
    nodeDesignUpdate: ((designData: NodeDesignData) => Promise<void>) | undefined,
    saveNodeDesign: (designData: NodeDesignData) => Promise<void>
  ): Promise<boolean> {
    if (!this.state.isNodeSizing) return false;

    const nodeDimensions = this.dimensions;

    this.state.isMouseDown = false;
    this.state.isNodeSizing = false;

    if (nodeDimensions.width) nodeDimensions.width = Math.round(nodeDimensions.width);
    if (nodeDimensions.height) nodeDimensions.height = Math.round(nodeDimensions.height);

    if (activeNode !== null && nodeDesignUpdate) {
      await nodeDesignUpdate(nodeDimensions);
      await saveNodeDesign(nodeDimensions);

      this.dimensions = {};
    }

    return true;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Change node's size by drag depending on mouse coordinates and active node sizer point.
   */
  private nodeSizeChange(
    node: Node,
    movementX: number,
    movementY: number,
    isShiftPressed: boolean,
    pixelRatio: number,
    aspectRatio: number,
    nodeDesignUpdate: (designData: NodeDesignData) => void
  ): void {
    const nodeData: NodeDesignData = {};
    // position.value extracts the string (e.g. 'top-left') from the SIZER_POINTS constant
    const position = this.state.hoveredSizerPoint?.position?.value;
    const sizerPointXMultiplier = this.getSizerPointMultiplier({ position, movementX });
    const sizerPointYMultiplier = this.getSizerPointMultiplier({ position, movementY });

    const shapeDef = getShape(node.shape);

    // Handle constrained shapes (circle: width === height)
    if (shapeDef.resize.constrainDimensions) {
      const diff = movementX || movementY;
      const multiplier = movementX ? sizerPointXMultiplier : sizerPointYMultiplier;
      Object.assign(
        nodeData,
        {
          width: Math.abs(node.width + diff * multiplier * 2 / pixelRatio) || 1,
          height: Math.abs(node.height + diff * multiplier * 2 / pixelRatio) || 1
        }
      );

      // If node width exceeds the max limit
      if (nodeData.width && nodeData.width > this.MAX_NODE_SIZE) {
        nodeData.width = this.MAX_NODE_SIZE;
        nodeData.height = this.MAX_NODE_SIZE;
      }
    }

    // Handle unconstrained shapes (rectangle, triangle, etc.)
    if (!shapeDef.resize.constrainDimensions) {
      const result: NodeDesignData = {};
      const keepAspectRatio = isShiftPressed;

      if (movementX) {
        const value = movementX * sizerPointXMultiplier;
        result.width = Math.abs(node.width + value * 2 / pixelRatio) || 1;
        if (result.width > this.MAX_NODE_SIZE) result.width = this.MAX_NODE_SIZE;

        if (keepAspectRatio) {
          // aspectRatio is width / height
          if (aspectRatio >= 1) result.height = result.width / aspectRatio || 1;
          if (aspectRatio < 1) {
            if (result.width / aspectRatio > this.MAX_NODE_SIZE) {
              result.height = this.MAX_NODE_SIZE;
              result.width = this.MAX_NODE_SIZE * aspectRatio;
            }
            if (result.width / aspectRatio <= this.MAX_NODE_SIZE) {
              result.height = result.width / aspectRatio || 1;
            }
          }
        }
      }

      if (movementY) {
        const value = movementY * sizerPointYMultiplier;
        result.height = Math.abs(node.height + value * 2 / pixelRatio) || 1;
        if (result.height > this.MAX_NODE_SIZE) result.height = this.MAX_NODE_SIZE;

        if (keepAspectRatio) {
          // aspectRatio is width / height
          if (aspectRatio <= 1) result.width = result.height * aspectRatio || 1;
          if (aspectRatio > 1) {
            if (result.height * aspectRatio > this.MAX_NODE_SIZE) {
              result.width = this.MAX_NODE_SIZE;
              result.height = this.MAX_NODE_SIZE / aspectRatio;
            }
            if (result.height * aspectRatio <= this.MAX_NODE_SIZE) {
              result.width = result.height * aspectRatio || 1;
            }
          }
        }
      }

      Object.assign(nodeData, result);
    }

    // The Vuex action updates the node, then dispatches nodesUpdateConvertedEdgesRelated
    // which updates all connected edge properties (fromNodeWidth, etc.) and marks them for canvas update.
    nodeDesignUpdate(nodeData);
    Object.assign(this.dimensions, nodeData);
  }

  /**
   * Get sizer point multiplier based on position and direction.
   * Returns -1 for top-left / top-right(Y) / bottom-left(X) corners so the resize inverts.
   */
  private getSizerPointMultiplier({
    position,
    movementX = null,
    movementY = null
  }: {
    position: string | undefined;
    movementX?: number | null;
    movementY?: number | null;
  }): number {
    // Depends on point position and direction to return positive or negative multiplier
    let result = 1;
    if (position === SIZER_POINTS.TOP_LEFT.value) result = -1;
    if (position === SIZER_POINTS.TOP_RIGHT.value && movementY !== null) result = -1;
    if (position === SIZER_POINTS.BOTTOM_LEFT.value && movementX !== null) result = -1;
    return result;
  }
}
